import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  INSPECTION_STATUS_LABELS,
  type InspectionStatus,
} from '@/services/inspection.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { SearchBar } from '@/components/shared/SearchBar';
import { Pagination } from '@/components/shared/Pagination';
import { EmptyState } from '@/components/shared/EmptyState';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

const STATUS_BADGE: Record<string, string> = {
  ASSIGNED: 'bg-blue-500/10 text-blue-700',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700',
  COMPLETED: 'bg-teal-500/10 text-teal-700',
};

const ACTIVE_STATUSES: InspectionStatus[] = ['ASSIGNED', 'IN_PROGRESS'];

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function TeamQueuePage() {
  const can = useHasPermission();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['team-queue', page, debouncedSearch],
    queryFn: () =>
      inspectionService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
  });

  const unclaimMutation = useMutation({
    mutationFn: (id: string) => inspectionService.unclaim(id),
    onSuccess: () => {
      toast.success('Inspection returned to job pool.');
      void qc.invalidateQueries({ queryKey: ['team-queue'] });
      void qc.invalidateQueries({ queryKey: ['job-pool'] });
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to unclaim inspection')),
  });

  const teamItems =
    data?.data.filter((i) => (ACTIVE_STATUSES as string[]).includes(i.status)) ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Team Queue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          All active inspections across the team. Manage assignments and returns.
        </p>
      </div>

      <SearchBar
        value={search}
        onChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        placeholder="Search by inspection number, equipment, engineer, or customer…"
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          Loading team queue…
        </div>
      ) : teamItems.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[800px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Inspection #</th>
                  <th className="px-4 py-3 font-medium text-foreground">Equipment</th>
                  <th className="px-4 py-3 font-medium text-foreground">Assigned To</th>
                  <th className="px-4 py-3 font-medium text-foreground">Customer</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  {can('inspection:unclaim') && (
                    <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {teamItems.map((insp) => (
                  <tr
                    key={insp.id}
                    className="border-b border-border last:border-0 hover:bg-muted/20"
                  >
                    <td
                      className="px-4 py-3 font-mono text-xs font-medium text-primary hover:underline cursor-pointer"
                      onClick={() => navigate(ROUTES.INSPECTION_DETAIL.replace(':id', insp.id))}
                    >
                      {insp.inspectionNumber}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{insp.equipmentName}</div>
                      <div className="text-xs text-muted-foreground">
                        {insp.equipmentAssetNumber}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {insp.assignedEngineerName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {insp.customerOrganizationName ?? insp.customerName ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[insp.status] ?? 'bg-muted text-muted-foreground'}`}
                      >
                        {INSPECTION_STATUS_LABELS[insp.status]}
                      </span>
                    </td>
                    {can('inspection:unclaim') && (
                      <td className="px-4 py-3">
                        {insp.status === 'ASSIGNED' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => unclaimMutation.mutate(insp.id)}
                            disabled={unclaimMutation.isPending}
                          >
                            Return to Pool
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data && (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              onPageChange={setPage}
            />
          )}
        </>
      ) : isError ? (
        <QueryErrorState subject="the team queue" onRetry={() => void refetch()} />
      ) : (
        <EmptyState
          icon={faUsers}
          title={search ? 'No inspections match your search' : 'No active team inspections'}
          description={
            search
              ? 'Try a different search term.'
              : 'No inspections are currently assigned or in progress.'
          }
        />
      )}
    </div>
  );
}
